import { type Mutation, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useMemo } from "react";
import { CashSettingsController } from "../../../api/src/cash-settings/cash-settings.controller";
import type {
  AccountCashSetting,
  CashSetting,
  CashTargetCondition,
  CashTargetType,
} from "../../../api/src/cash-settings/lib";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { RecurringTransferFrequency } from "../../../api/src/transfers/transfers.service";
import InfoTooltip from "../components/InfoTooltip";
import { useAuthenticatedMutationNew, useAuthenticatedQuery } from "../lib/api";
import { naLabel } from "../lib/display";
import { useAuthenticatedFetchAllPages } from "../lib/pagination";
import { formatCurrency, formatPercent } from "../lib/numbers";

export function getCashTarget(
  accountBalance: number,
  cashTargetType: CashTargetType,
  cashTargetAbsolute?: number | null,
  cashTargetPercent?: number | null,
) {
  return cashTargetType === "percent"
    ? ((cashTargetPercent ?? 0) / 100) * accountBalance
    : accountBalance === 0
      ? 0
      : (cashTargetAbsolute ?? 0);
}

export function getCashTargetPercentage(
  accountBalance: number,
  cashTargetType: CashTargetType,
  cashTargetAbsolute?: number | null,
  cashTargetPercent?: number | null,
) {
  return cashTargetType === "percent"
    ? (cashTargetPercent ?? 0) / 100
    : accountBalance === 0
      ? 0
      : (cashTargetAbsolute ?? 0) / accountBalance;
}

export function displayCashTarget(
  accountBalance: number,
  cashTargetType: CashTargetType,
  cashTargetAbsolute?: number | null,
  cashTargetPercent?: number | null,
) {
  if (
    (cashTargetType === "absolute" && cashTargetAbsolute == null) ||
    (cashTargetType === "percent" && cashTargetPercent == null)
  ) {
    return naLabel;
  }

  const cashTarget = getCashTarget(
    accountBalance,
    cashTargetType,
    cashTargetAbsolute,
    cashTargetPercent,
  );
  const percentageValue = getCashTargetPercentage(
    accountBalance,
    cashTargetType,
    cashTargetAbsolute,
    cashTargetPercent,
  );

  let primaryValue: string | undefined;
  let secondaryValue = "";
  if (cashTargetType === "absolute") {
    primaryValue = formatCurrency(cashTarget);
    secondaryValue = formatPercent(percentageValue, 2);
  } else if (cashTargetType === "percent") {
    primaryValue = formatPercent(percentageValue, 2);
    secondaryValue = formatCurrency(cashTarget);
  }

  return typeof primaryValue === "undefined"
    ? naLabel
    : `${primaryValue} (${secondaryValue})`;
}

export function displayMinMaxCashTarget(
  accountBalance: number,
  cashTargetCondition: CashTargetCondition,
  cashTargetAbsolute?: number | null,
  cashTargetPercent?: number | null,
) {
  if (cashTargetAbsolute == null && cashTargetPercent == null) {
    return naLabel;
  } else if (cashTargetAbsolute == null) {
    return displayCashTarget(
      accountBalance,
      "percent",
      cashTargetAbsolute,
      cashTargetPercent,
    );
  } else if (cashTargetPercent == null) {
    return displayCashTarget(
      accountBalance,
      "absolute",
      cashTargetAbsolute,
      cashTargetPercent,
    );
  } else {
    const absoluteDisplay = displayCashTarget(
      accountBalance,
      "absolute",
      cashTargetAbsolute,
      cashTargetPercent,
    );
    const percentDisplay = displayCashTarget(
      accountBalance,
      "percent",
      cashTargetAbsolute,
      cashTargetPercent,
    );

    return `${absoluteDisplay} ${cashTargetCondition} ${percentDisplay}`;
  }
}

export function displayForecastedTransferFrequency(
  frequency: RecurringTransferFrequency,
) {
  switch (frequency) {
    case "first":
      return "First";
    case "annually":
      return "Annually";
    case "biweekly":
      return "Every Two Weeks";
    case "monthly":
      return "Monthly";
    case "quarterly":
      return "Quarterly";
    case "semiannually":
      return "Semi-Annually";
    case "other":
      return "Other";
    default:
      throw new Error(`Unknown frequency type ${frequency}`);
  }
}

/**
 * The symbol a custodian reports plain cash under (`CUSTODIAN_CASH_SYMBOL`,
 * `lib.py:20` in `AdviceCloud/python`, spec R16). R15 admits ANY security as a
 * cash split except this one, because `CASH-1` is how a custodian reports plain
 * cash rather than a security an advisor holds, so naming it would make one
 * vehicle serve as both.
 */
export const CUSTODIAN_CASH_SYMBOL = "CASH-1";

/**
 * Folded, because `account.cash_split_symbol` is stored as typed
 * (an-gap-01a06212e3b492b8) and `SymbolField` is free-text autosuggest rather
 * than a constrained select, so `cash-1` reaches the api as readily as
 * `CASH-1`. The api folds the same comparison.
 */
export const isCustodianCashSymbol = (cashSplitSymbol: unknown) =>
  typeof cashSplitSymbol === "string" &&
  cashSplitSymbol.trim().toUpperCase() === CUSTODIAN_CASH_SYMBOL;

/**
 * The sentence R15's refusal puts in front of an advisor. It is the api's
 * wording for the same refusal (`validateCashSplitPair`,
 * `api/src/cash-settings/cash-settings.service.ts`), so a rejection that
 * reaches the server and one caught in the browser read alike.
 *
 * These three sit here rather than in either form because BOTH forms refuse
 * the symbol: the api refuses it for the two `cash_setting` writers as well as
 * for `updateAccount`, so the firm cash-settings form
 * (`client/src/cash/CashSettingInfo.tsx`) carries the clause beside the
 * account form's (`client/src/cash/AccountCashInfo.tsx`). Importing one
 * component module from the other is the alternative.
 */
export const CUSTODIAN_CASH_REFUSED = `${CUSTODIAN_CASH_SYMBOL} cannot be the Cash Split. It is how a custodian reports plain cash, not a security held alongside it.`;

/**
 * The sentence R11's refusal puts in front of an advisor. It names the form's
 * own field label and says which value is missing, rather than calling the
 * pair invalid.
 *
 * It is the api's wording for the same refusal, down to the full stop
 * (`cashSplitPairRefusal`, `api/src/cash-settings/cash-settings.service.ts`),
 * so a rejection that reaches the server and one caught in the browser read
 * alike.
 *
 * It sits here for the same reason CUSTODIAN_CASH_REFUSED does — both forms
 * refuse a split percentage with no cash split beside it, so both put this
 * sentence in front of the advisor.
 */
export const CASH_SPLIT_REQUIRED =
  "Cash Split is required when a Cash Split % of Cash Target is set.";

/**
 * Whether the field names a cash split at all.
 *
 * Trimmed, because the api trims before deciding the same question
 * (`validateCashSplitPair`, `api/src/cash-settings/cash-settings.service.ts`)
 * and a form that called "   " a cash split would refuse a save the api would
 * have accepted. Typed `unknown` because a test reads it off `this.parent`,
 * where nothing narrows it.
 */
export const hasCashSplit = (cashSplitSymbol: unknown) =>
  typeof cashSplitSymbol === "string" && cashSplitSymbol.trim() !== "";

/**
 * Hover help for the Cash Split % of Cash Target and Cash Split fields and columns
 * (doc-cash-split-01a05d5f47df200b). The percentage is a share of the cash
 * target, and a missing percentage defaults to 50% (R17), as
 * `ResolvedCashSplit` in `python/src/rebalance/cash_settings.py` resolves it.
 */
export const cashSplitPercentHelpMessage =
  "The share of this account's cash target held in the Cash Split instead of cash. Example: with a 12% cash target and a Cash Split % of Cash Target of 80%, 9.6% of the account is held in the Cash Split and 2.4% as cash. If this is blank, rebalancing uses 50%.";
export const cashSplitSymbolHelpMessage =
  "A security, usually a money market fund, that holds part of this account's cash target. It cannot be a primary security in the account's model.";
/**
 * Appended to both messages where they describe a firm cash setting rather
 * than an account, because a rebalance reads the account's values (§4.1).
 */
export const cashSettingTemplateHelpNote =
  "Choosing this cash setting on an account copies these values to the account. Rebalancing uses the account's values.";

/**
 * A help message followed by `cashSettingTemplateHelpNote`, for the places
 * that show a firm cash setting's values rather than an account's.
 */
export const CashSettingTemplateHelp = ({ message }: { message: string }) => (
  <>
    <p className="mb-1">{message}</p>
    <p className="mb-0">{cashSettingTemplateHelpNote}</p>
  </>
);

/**
 * A table column header with an info icon that shows `tooltip` on hover.
 * `Table` renders the header inside the sort toggle's click target, so the
 * icon stops the click there: a click on the icon must not sort the column.
 */
export const ColumnHeaderWithHelp = ({
  label,
  tooltip,
}: {
  label: string;
  tooltip: ReactNode;
}) => (
  <>
    <span className="align-middle">{label}</span>
    <InfoTooltip
      tooltip={tooltip}
      buttonProps={{
        width: 16,
        className: "ms-1 align-middle",
        onClick: (event) => event.stopPropagation(),
      }}
    />
  </>
);

/**
 * Whether a model's primary securities name the cash split configured on an
 * account.
 *
 * R6 (doc-cash-split-01a05d5f47df200b): a model containing the cash split
 * cannot be assigned to an account configured with that cash split. The
 * comparison set is the model's direct primary securities -- not its sleeves
 * and not its security groups -- which is also what §4.1 rejects on.
 *
 * Neither value is guaranteed canonical upper case. account.cash_split_symbol
 * is stored as typed, and security_base.symbol is written verbatim from
 * BridgeFT's `identifier` by writeSecuritiesToDB, with no upper() and no trim
 * (api/src/lib/cash-split.ts, "HOW IT IS COMPARED"). Both sides are therefore
 * trimmed and upper-cased before they are compared, which is what keeps a
 * lower-case or padded value on either side from slipping past the check;
 * normalising either column is a separate gap (an-gap-01a06212e3b492b8).
 */
export function modelContainsCashSplit(
  primarySecurities: readonly { symbol: string }[] | undefined,
  cashSplitSymbol: string | null | undefined,
) {
  const cashSplit = cashSplitSymbol?.trim().toUpperCase();

  return (
    typeof primarySecurities !== "undefined" &&
    typeof cashSplit !== "undefined" &&
    cashSplit !== "" &&
    primarySecurities.some(
      (primarySecurity) =>
        primarySecurity.symbol.trim().toUpperCase() === cashSplit,
    )
  );
}

export const CashSettingsHelp = () => (
  <section>
    <p>
      Cash Settings are used to define the target cash balance for an account.
      They have three components to them: target value, min threshold, and max
      threshold. The target value is the desired cash balance for the account.
      The minimum and maximum thresholds are used to trigger rebalances.
    </p>
    <h3>Cash Target</h3>
    <p>
      The cash target is used in the target model for an account when
      calculating rebalances. Cash targets sit <em>alongside</em> an assigned
      model, meaning the target model will be applied to the total balance of
      the account minus the target cash value. Cash targets should <em>not</em>{" "}
      be used as an asset allocation mechanism, for example managing risk. They
      should only be used to manage liquidity, such as for management fees and
      other planned expenses.
    </p>
  </section>
);

/**
 * The path of `getCashSettings`. Also the FIRST element of every react-query
 * key the page walk in `useQueryCashSettings` uses, so `[CASH_SETTINGS_PATH]`
 * reaches every page.
 */
export const CASH_SETTINGS_PATH = "/v1/cash-settings";

/** One item of `GET /v1/cash-settings`, as the route serialises it. */
export type SerializedCashSettingListItem = SerializedObject<
  UnpackResponse<CashSettingsController["getCashSettings"]>
>["data"][number];

/**
 * The cash-setting writes a mounted list shows, by the mutation keys this file
 * gives them: the create of `useCreateCashSetting`
 * (`["POST", "v1", "cash-settings"]`), the update of `useUpdateCashSetting`
 * (`["PUT", "v1", "cash-settings", cashSettingId]`) and the delete of
 * `useDeleteCashSetting`
 * (`["DELETE", "v1", "cash-settings", deleteCashSettingId, newCashSettingId]`).
 * The account cash-setting writes change no cash setting, so they are not
 * among them.
 */
const isCashSettingListWrite = (
  mutation: Mutation<unknown, unknown, unknown>,
) => {
  const key = mutation.options.mutationKey ?? [];
  const [method, version, collection] = key;
  if (version !== "v1" || collection !== "cash-settings") {
    return false;
  }
  return (
    (method === "POST" && key.length === 3) ||
    (method === "PUT" && key.length === 4 && typeof key[3] === "number") ||
    (method === "DELETE" && key.length === 5 && typeof key[3] === "number")
  );
};

/**
 * Every cash setting of the firm. `GET /v1/cash-settings` answers a page
 * (APP-1651), and every screen that reads the list shows, picks from or looks
 * up the whole of it (`CashSettings.tsx`, `CashSettingInfo.tsx`,
 * `AccountCashInfo.tsx`, `AccountCashSettings.tsx`,
 * `HouseholdCashBilling.tsx`), so this walks every page
 * (`useAuthenticatedFetchAllPages`) and holds `data` back until the last one
 * is in. `enabled: false` holds the walk before its first request, and
 * `isPending` stays `true` then, as it did for the disabled query before.
 *
 * The four percent fields reach the screens as the route sends them, on 0 to
 * 100 as stored; nothing here converts them.
 *
 * When a create, update or delete (`isCashSettingListWrite`) settles, the
 * cached pages are dropped and the walk starts again, so a mounted list shows
 * it. When the last reader of the list unmounts, the cached pages are dropped
 * too: a reader mounted later walks the list again rather than collecting
 * pages cached before a write. Both follow `useQueryFeeStructures`
 * (`billing/lib.tsx`).
 */
export function useQueryCashSettings({ enabled = true } = {}) {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError, reload } =
    useAuthenticatedFetchAllPages<SerializedCashSettingListItem>(
      CASH_SETTINGS_PATH,
      {},
      { enabled },
    );

  // Restart the walk when a write settles. See `useQueryFeeStructures` for why
  // this subscribes to the mutation cache's events.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isCashSettingListWrite(event.mutation)
        ) {
          queryClient.removeQueries({ queryKey: [CASH_SETTINGS_PATH] });
          reload();
        }
      }),
    [queryClient, reload],
  );

  // On unmount, drop the pages no other mounted reader still observes. This
  // cleanup runs after the walk's own observer has unsubscribed, because the
  // walk's hooks are called above it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [CASH_SETTINGS_PATH],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient],
  );

  const data: { data: SerializedCashSettingListItem[] } | undefined = useMemo(
    () => (hasEveryRow ? { data: rows } : undefined),
    [rows, hasEveryRow],
  );

  const isPending = !hasEveryRow && !isError;

  return { isPending, isLoading: isPending, isError, data };
}

/**
 * The path of `getAllAccountSettings`. Also the FIRST element of every
 * react-query key the page walk in `useQueryAccountCashSettings` uses, so
 * `[ACCOUNT_CASH_SETTINGS_PATH]` reaches every page. It is not reached by
 * `[CASH_SETTINGS_PATH]`: a key matches on whole elements.
 */
export const ACCOUNT_CASH_SETTINGS_PATH = "/v1/cash-settings/accounts";

/** One item of `GET /v1/cash-settings/accounts`, as the route serialises it. */
export type SerializedAccountCashSettingListItem = SerializedObject<
  UnpackResponse<CashSettingsController["getAllAccountSettings"]>
>["data"][number];

/**
 * Every account cash setting of the firm, or, with `requireBalance`, of every
 * account with a balance above zero. `GET /v1/cash-settings/accounts` answers
 * a page (APP-1651), and every screen that reads the list keys it by account
 * or counts it whole (`AccountCashSettings.tsx`, `HouseholdCashBilling.tsx`,
 * `HouseholdCashBillingRebalance.tsx`, `UnassignedCashSettings.tsx`,
 * `useInsightAccountIds` in `insightRegistry.ts`), so this walks every page
 * (`useAuthenticatedFetchAllPages`) and holds `data` back until the last one
 * is in. `requireBalance` is sent as the route's query parameter on every page,
 * so the server filters before it cuts a page; nothing here filters. `enabled:
 * false` holds the walk before its first request, and `isPending` stays `true`
 * then, as `useQueryCashSettings` does.
 *
 * The four percent fields reach the screens as the route sends them, on 0 to
 * 100 as stored; nothing here converts them.
 *
 * Each row's target columns resolve through the assigned cash setting
 * (`getAllAccountCashSettingsForFirm`), so when a cash-setting update or
 * delete (`isCashSettingListWrite`) settles, the cached pages are dropped and
 * the walk starts again. The account cash-setting writes
 * (`PUT /v1/cash-settings/accounts` and `PUT /v1/cash-settings/accounts/:id`)
 * are sent through `useAuthenticatedMutationAsync`, which no mutation cache
 * records, so a screen that sends one on a mounted list calls `reload`. When
 * the last reader of the list unmounts, the cached pages are dropped too: a
 * reader mounted later walks the list again rather than collecting pages
 * cached before a write. Both follow `useQueryCashSettings` above.
 */
export function useQueryAccountCashSettings({
  requireBalance = false,
  enabled = true,
}: { requireBalance?: boolean; enabled?: boolean } = {}) {
  const queryClient = useQueryClient();
  const {
    rows,
    hasEveryRow,
    isError,
    reload: restartWalk,
  } = useAuthenticatedFetchAllPages<SerializedAccountCashSettingListItem>(
    ACCOUNT_CASH_SETTINGS_PATH,
    requireBalance ? { requireBalance: "true" } : {},
    { enabled },
  );

  /** Drop every cached page and walk the list again. */
  const reload = useCallback(() => {
    queryClient.removeQueries({ queryKey: [ACCOUNT_CASH_SETTINGS_PATH] });
    restartWalk();
  }, [queryClient, restartWalk]);

  // Restart the walk when a cash-setting write settles. See
  // `useQueryFeeStructures` for why this subscribes to the mutation cache's
  // events.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (
          event.type === "updated" &&
          event.action.type === "success" &&
          isCashSettingListWrite(event.mutation)
        ) {
          reload();
        }
      }),
    [queryClient, reload],
  );

  // On unmount, drop the pages no other mounted reader still observes. This
  // cleanup runs after the walk's own observer has unsubscribed, because the
  // walk's hooks are called above it.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: [ACCOUNT_CASH_SETTINGS_PATH],
        predicate: (query) => query.getObserversCount() === 0,
      });
    },
    [queryClient],
  );

  const data: { data: SerializedAccountCashSettingListItem[] } | undefined =
    useMemo(
      () => (hasEveryRow ? { data: rows } : undefined),
      [rows, hasEveryRow],
    );

  const isPending = !hasEveryRow && !isError;

  return { isPending, isLoading: isPending, isError, data, reload };
}

export function useQueryCashSetting(id: number, { enabled = true } = {}) {
  return useAuthenticatedQuery<
    UnpackResponse<CashSettingsController["getCashSetting"]>,
    CashSetting,
    [string, string, number]
  >({
    queryKey: ["v1", "cash-settings", id],
    queryFn: async (cashSettings) => cashSettings.data,
    queryOptions: { enabled },
  });
}

/**
 * Creates a cash setting and resolves to its id. `POST /v1/cash-settings`
 * answers the created cash setting (APP-1651); the id is what
 * `CashSettingInfo.tsx` navigates to.
 */
export function useCreateCashSetting() {
  return useAuthenticatedMutationNew<
    UnpackResponse<CashSettingsController["createCashSetting"]>,
    number,
    Omit<CashSetting, "id">
  >({
    mutationKey: ["POST", "v1", "cash-settings"],
    mutationFn: ({ data }) => data.id,
  });
}

export function useUpdateCashSetting(cashSettingId: number) {
  return useAuthenticatedMutationNew<
    UnpackResponse<CashSettingsController["updateCashSetting"]>,
    void,
    Omit<CashSetting, "id">
  >({
    mutationKey: ["PUT", "v1", "cash-settings", cashSettingId],
    mutationFn: () => null,
  });
}

/**
 * Deletes a cash setting, first moving the firm's accounts on it to
 * `newCashSettingId` (`DELETE /v1/cash-settings/:id?newCashSettingId=`,
 * APP-1651). The API does both in one transaction. `null`, the dialog's empty
 * selection, is sent as an empty `newCashSettingId`, which clears the
 * accounts' cash setting, as `PUT /cash-settings/:id/replace` did with a
 * `null` in its body.
 */
export function useDeleteCashSetting(
  deleteCashSettingId: number,
  newCashSettingId: number | null,
) {
  return useAuthenticatedMutationNew<
    UnpackResponse<CashSettingsController["delete"]>,
    UnpackResponse<CashSettingsController["delete"]>["data"],
    void
  >({
    mutationKey: [
      "DELETE",
      "v1",
      "cash-settings",
      deleteCashSettingId,
      newCashSettingId,
    ],
    mutationFn: ({ data }) => data,
    // The key parts become path segments by default (`lib/api.ts`), so the
    // query string is built here rather than put into a key part.
    urlBuilderFn: ([version, collection, id, replacementId]) =>
      `/${version}/${collection}/${id}?newCashSettingId=${replacementId ?? ""}`,
  });
}

export function useQueryCashSettingAssignmentCounts(
  id: number,
  { enabled = true } = {},
) {
  return useAuthenticatedQuery<
    UnpackResponse<
      CashSettingsController["getCashSettingAccountAssignmentCount"]
    >,
    number,
    [string, string, number, string]
  >({
    queryKey: ["v1", "cash-settings", id, "assignments-count"],
    queryFn: async (cashSettings) => cashSettings.data.accountsAssignmentCount,
    queryOptions: { enabled },
  });
}

export function useQueryAccountCashSetting(
  id: number,
  { enabled = true } = {},
) {
  return useAuthenticatedQuery<
    UnpackResponse<CashSettingsController["getAccountSetting"]>,
    AccountCashSetting,
    [string, string, string, number]
  >({
    queryKey: ["v1", "cash-settings", "accounts", id],
    queryFn: async (cashSettings) => cashSettings.data,
    queryOptions: { enabled },
  });
}
